import { Component, h, Host, Prop } from '@stencil/core';

import { BoxRoundedPreset } from '../../../services/interfaces';
import { getCSSVariable } from '../../../services/utils';

/**
 * @part base - The `<div></div>` element container inside the Shadow DOM
 */
@Component({
  tag: 'pk-box',
  styleUrl: './scss/pk-box.scss',
  shadow: true,
})
export class PkBox {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** If `true`, all paddings inside the box container will be removed */
  @Prop({ reflect: true }) paddingNone: boolean = false;

  /** Specify the value of the corner radius
   * (it should be a valid Panenka --border-radius token value) */
  @Prop({ reflect: true }) rounded: BoxRoundedPreset = 'xl';

  /** If `true`, a box shadow will applied to the box container */
  @Prop({ reflect: true }) shadow: boolean = false;

  /** If `false`, the stroke border style will be removed */
  @Prop({ reflect: true }) stroke: boolean = true;

  /** Set a background color. The value should be a valid value of the palette color */
  @Prop({ reflect: true }) backgroundColor: string;

  // Prop lifecycle events
  // =======================

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const styles = {
      ...(this.rounded && { '--pk-box--border-radius': getCSSVariable(`border-radius-${this.rounded}`) }),
      ...(this.backgroundColor && { '--pk-box--background-color': getCSSVariable(this.backgroundColor) }),
    };

    const cssClasses = {
      'pk-box': true,
      'has-shadow': this.shadow,
      'has-stroke': this.stroke,
      'no-padding': this.paddingNone,
    };

    return (
      <Host style={styles}>
        <div class={cssClasses} part="base">
          <slot />
        </div>
      </Host>
    );
  }
}
